import { useEffect, useState } from 'react'
import { api, type RetentionStatus } from '../api'

/**
 * Retention — IMPLEMENTATION.md decision 35.
 *
 * How long snapshot positions, evaluation runs and the audit trail are kept, what
 * the next removal would take, how much space each takes now, and a forecast for a
 * book of a given shape over five, ten and twenty years.
 *
 * **Setting a period is here; removing records is not.** Removal cannot be undone,
 * and it runs on the server (`anubandh apply-retention`), where the firm takes its
 * backup first, for the same reason schema migration does. A period never goes
 * below its category's floor.
 *
 * The forecast uses the bytes per record the server reports, which were measured on
 * real snapshots and verdicts, indexes included.
 */

const HORIZONS = [5, 10, 20]
const YEARS = [1, 2, 3, 5, 6, 7, 8, 10, 12, 15, 20, 25, 30]

export interface Book {
  funds: number
  positions: number
  limits: number
  runsPerDay: number
  users: number
}

/** Bytes after each horizon, everything kept and under the retention periods. The same
 *  arithmetic as `retention.forecast` on the server. */
export function forecast(
  c: RetentionStatus['coefficients'],
  book: Book,
  keep: Record<string, number>,
): { years: number; whole: number; kept: number }[] {
  const days = c.business_days
  const yearly: Record<string, number> = {
    snapshot_positions:
      (book.funds * book.positions * c.bytes.position + book.funds * c.bytes.snapshot) * days,
    evaluation_runs: book.funds * book.runsPerDay * (c.bytes.run + book.limits * c.bytes.verdict) * days,
    audit_trail:
      (book.funds * (1 + book.runsPerDay) + book.users * c.audit_events_per_user_per_day) *
      c.bytes.audit_event *
      days,
  }
  return HORIZONS.map((years) => ({
    years,
    whole: Object.values(yearly).reduce((sum, v) => sum + v * years * c.overhead, 0),
    kept: Object.entries(yearly).reduce(
      (sum, [k, v]) => sum + v * Math.min(years, keep[k] ?? years) * c.overhead,
      0,
    ),
  }))
}

export function size(bytes: number): string {
  const units: [number, string][] = [
    [1024 ** 4, 'TB'],
    [1024 ** 3, 'GB'],
    [1024 ** 2, 'MB'],
  ]
  for (const [scale, unit] of units) {
    if (bytes >= scale) return `${(bytes / scale).toFixed(bytes / scale < 10 ? 1 : 0)} ${unit}`
  }
  return `${Math.max(1, Math.round(bytes / 1024))} KB`
}

function said(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

const BOOK_FIELDS: [keyof Book, string][] = [
  ['funds', 'Funds'],
  ['positions', 'Positions per fund'],
  ['limits', 'Limits per fund'],
  ['runsPerDay', 'Evaluations per fund a day'],
  ['users', 'Users'],
]

type View = 'rules' | 'forecast'
const VIEWS: [View, string][] = [
  ['rules', 'Rules'],
  ['forecast', 'Forecasting'],
]

export function Retention() {
  const [status, setStatus] = useState<RetentionStatus | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState<string | null>(null)
  const [book, setBook] = useState<Book>({ funds: 50, positions: 300, limits: 30, runsPerDay: 2, users: 30 })
  const [view, setView] = useState<View>('rules')

  async function load() {
    try {
      setStatus(await api.retention())
    } catch (e) {
      setError(said(e))
    }
  }

  useEffect(() => {
    void load()
  }, [])

  async function change(category: string, years: number) {
    setSaving(category)
    setError(null)
    try {
      await api.setRetention(category, years)
      await load()
    } catch (e) {
      setError(said(e))
    } finally {
      setSaving(null)
    }
  }

  if (!status) {
    return error ? (
      <p className="error" role="alert">
        {error}
      </p>
    ) : (
      <p className="loading">Loading…</p>
    )
  }

  const keep = Object.fromEntries(status.policy.map((p) => [p.category, p.years]))
  const projected = forecast(status.coefficients, book, keep)
  const c = status.coefficients

  return (
    <div className="card retention" data-testid="retention">
      {/* The periods and the space forecast apart: one is a setting somebody comes to
          change, the other a calculation somebody comes to play with. */}
      <div className="tabstrip sub" role="tablist" aria-label="Retention">
        {VIEWS.map(([id, label]) => (
          <button key={id} role="tab" aria-selected={view === id} onClick={() => setView(id)}>
            {label}
          </button>
        ))}
      </div>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {view === 'rules' ? (
        <>
          <h3>How long records are kept</h3>
          <p className="note">
            Set each to your firm’s record-keeping obligations. A period cannot be set below its floor.
            Removal runs on the server: <code>anubandh apply-retention</code> shows what it would remove,
            and with <code>--yes</code> removes it, after your backup.
          </p>
          <div className="table-scroll">
            <table className="grid stacked" data-testid="retention-policy">
              <thead>
                <tr>
                  <th>Records</th>
                  <th>Keep for</th>
                  <th>Next removal</th>
                  <th>Space now</th>
                </tr>
              </thead>
              <tbody>
                {status.policy.map((p) => {
                  const next = status.preview.categories.find((x) => x.category === p.category)
                  const used = status.usage.categories[p.category]
                  const choices = [...new Set([...YEARS, p.years])].filter((y) => y >= p.minimum).sort((a, b) => a - b)
                  return (
                    <tr key={p.category}>
                      <td>
                        <strong>{p.label}</strong>
                        <span className="clause">{p.covers}</span>
                      </td>
                      <td>
                        <select
                          aria-label={`Keep ${p.label.toLowerCase()} for`}
                          value={p.years}
                          disabled={saving === p.category}
                          onChange={(e) => void change(p.category, Number(e.target.value))}
                        >
                          {choices.map((y) => (
                            <option key={y} value={y}>
                              {y} year{y === 1 ? '' : 's'}
                            </option>
                          ))}
                        </select>
                        <span className="clause">
                          At least {p.minimum} year{p.minimum === 1 ? '' : 's'}
                          {p.updated_by ? ` · set by ${p.updated_by}` : ''}
                        </span>
                      </td>
                      <td>
                        {next && next.rows > 0
                          ? `${next.rows.toLocaleString()} rows from before ${next.before}, about ${size(next.bytes)}`
                          : `Nothing from before ${next?.before ?? ''}`}
                        {next?.unsealed ? (
                          <span className="clause">
                            {next.unsealed.toLocaleString()} older audit events are not sealed yet, and are kept
                          </span>
                        ) : null}
                      </td>
                      <td>
                        {used ? size(used.bytes) : ''}
                        {used && (
                          <span className="clause">
                            {used.rows.toLocaleString()} rows · growing about {size(used.bytes_per_year)} a year
                          </span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <p className="note" data-testid="retention-kept">
            Kept for the life of the appliance: {status.kept.join('; ')}.
          </p>
        </>
      ) : (
        <>
          <p>This database takes {size(status.usage.database_bytes)} today.</p>
          <h3>Forecast for a book like yours</h3>
          <div className="filters">
            {BOOK_FIELDS.map(([field, label]) => (
              <label key={field}>
                {label}
                <input
                  type="number"
                  min={0}
                  value={book[field]}
                  onChange={(e) => setBook({ ...book, [field]: Math.max(0, Number(e.target.value) || 0) })}
                />
              </label>
            ))}
          </div>
          <div className="table-scroll">
            <table className="grid" data-testid="retention-forecast">
              <thead>
                <tr>
                  <th>After</th>
                  <th className="num">Everything kept</th>
                  <th className="num">With the periods under Rules</th>
                </tr>
              </thead>
              <tbody>
                {projected.map((row) => (
                  <tr key={row.years}>
                    <td>{row.years} years</td>
                    <td className="num">{size(row.whole)}</td>
                    <td className="num">{size(row.kept)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="note">
            Every business day ({c.business_days} a year) each fund loads one snapshot and is evaluated as
            often as set above. Measured at {c.bytes.position} bytes a position, {c.bytes.verdict} a verdict
            and {c.bytes.audit_event} an audit event, indexes included, with{' '}
            {Math.round((c.overhead - 1) * 100)}% added for free space and the write-ahead log. Backups and
            the uploads volume are extra.
          </p>
        </>
      )}
    </div>
  )
}
